import { useEffect, useState } from "react";
import { TopBar } from "@/components/PageShell";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Save, Cake, Folder, FileText, Mountain } from "lucide-react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [dir, setDir] = useState(null);

  const load = async () => { const r = await api.get("/settings"); setS(r.data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await api.put("/settings", s);
    toast.success("Configurações salvas");
  };

  const pickFolder = async () => {
    if (!window.showDirectoryPicker) {
      toast.info("Seu navegador não suporta seleção de pasta. Use o Google Chrome mais recente.");
      return;
    }
    try {
      const handle = await window.showDirectoryPicker({ mode: "readwrite" });
      setDir(handle.name);
      window.__hermes_dir_handle = handle;
      setS({ ...s, download_folder: handle.name });
      toast.success(`Pasta selecionada: ${handle.name}`);
    } catch (e) {
      // user cancelled
    }
  };

  if (!s) return <div className="p-8 text-slate-500">Carregando...</div>;

  return (
    <div className="flex-1 min-w-0 flex flex-col">
      <TopBar/>
      <div className="flex-1 p-3 sm:p-6 lg:p-8 font-sans">
      <h1 className="font-heading text-3xl font-semibold text-slate-900">Configurações</h1>
      <p className="text-slate-500 mt-1">Preferências do sistema para o seu escritório.</p>

      <section className="mt-8 bg-white rounded-2xl border border-slate-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Cake className="w-5 h-5 text-[#0f4c5c]"/>
          <h2 className="font-heading text-lg font-semibold">Aniversários</h2>
        </div>
        <label className="block">
          <div className="text-xs font-medium text-slate-600 mb-2">Quantos dias de antecedência para o alerta?</div>
          <div className="grid grid-cols-5 gap-2">
            {[1,2,3,4,5].map(n => (
              <button key={n} onClick={() => setS({ ...s, birthday_advance_days: n })} data-testid={`bday-${n}`}
                className={`h-10 rounded-lg border transition text-sm font-medium ${s.birthday_advance_days===n?'bg-slate-900 text-white border-slate-900':'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                {n} dia{n>1?'s':''}
              </button>
            ))}
          </div>
        </label>
        <p className="text-xs text-slate-500 mt-3">Um aviso aparecerá no Dashboard quando algum cliente estiver a até {s.birthday_advance_days} dia(s) do aniversário. Você pode gerar um cartão personalizado com IA.</p>
      </section>

      <section className="mt-6 bg-white rounded-2xl border border-slate-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Folder className="w-5 h-5 text-[#0f4c5c]"/>
          <h2 className="font-heading text-lg font-semibold">Salvamento local dos documentos assinados</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">Escolha uma pasta local no seu computador para onde os PDFs/DOCX assinados serão sugeridos no download. Disponível no Google Chrome/Edge.</p>
        <div className="flex items-center gap-3">
          <input readOnly value={s.download_folder || (dir ?? "")} placeholder="Nenhuma pasta selecionada"
            className="flex-1 h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm"/>
          <button onClick={pickFolder} data-testid="btn-pick-folder"
            className="px-4 h-10 rounded-lg gradient-primary text-white text-sm inline-flex items-center gap-2 hover:-translate-y-0.5 transition">
            <Folder className="w-4 h-4"/> Escolher pasta
          </button>
        </div>
      </section>

      <PasswordChangeSection/>

      <WeeklyPdfToggle/>

      <MineralDigestOptOutToggle/>

      <div className="mt-6 flex justify-end">
        <button onClick={save} data-testid="btn-save-settings"
          className="gradient-primary text-white px-6 h-11 rounded-xl inline-flex items-center gap-2 hover:-translate-y-0.5 transition text-sm font-medium">
          <Save className="w-4 h-4"/> Salvar
        </button>
      </div>
      </div>
    </div>
  );
}

function MineralDigestOptOutToggle() {
  const [enabled, setEnabled] = useState(false); // "enabled" = digest ATIVO (opt-out=false)
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setEnabled(!r.data?.mineral_digest_optout);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const toggle = async () => {
    const next = !enabled;
    setBusy(true);
    try {
      await api.patch("/me", { mineral_digest_optout: !next });
      setEnabled(next);
      toast.success(next ? "Digest semanal ativado" : "Digest semanal desativado");
    } catch { toast.error("Falha ao atualizar preferência"); }
    finally { setBusy(false); }
  };
  if (loading) return null;
  return (
    <section className="mt-6 bg-white rounded-2xl p-6 border border-slate-200 shadow-sm"
             data-testid="mineral-digest-toggle-section">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg inline-flex items-center justify-center shrink-0"
               style={{ background: "linear-gradient(135deg, #0B1B2F 0%, #071B34 100%)" }}>
            <Mountain className="w-5 h-5" style={{ color: "#F0D37A" }}/>
          </div>
          <div className="min-w-0">
            <h3 className="font-heading text-lg" style={{ color: "#0B1B2F" }}>
              Digest semanal do Radar Mineral
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              E-mail dominical em formato mini-jornal com todas as mudanças da semana nos processos minerários que você monitora.
            </p>
          </div>
        </div>
        <button onClick={toggle} disabled={busy}
                data-testid="btn-toggle-mineral-digest"
                aria-pressed={enabled}
                className={`relative shrink-0 w-14 h-7 rounded-full transition-colors ${enabled ? "" : "bg-slate-200"}`}
                style={enabled ? { background: "linear-gradient(180deg, #F5DE8C 0%, #D4AF37 100%)" } : undefined}>
          <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-7" : "translate-x-0.5"}`}/>
        </button>
      </div>
    </section>
  );
}

function WeeklyPdfToggle() {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setEnabled(!!r.data?.weekly_pdf_attach);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    const next = !enabled;
    setBusy(true);
    try {
      await api.patch("/me", { weekly_pdf_attach: next });
      setEnabled(next);
      toast.success(next ? "PDF será anexado ao resumo semanal" : "Anexo PDF desativado");
    } catch {
      toast.error("Falha ao atualizar preferência");
    } finally { setBusy(false); }
  };

  if (loading) return null;

  return (
    <section className="mt-6 bg-white rounded-2xl p-6 border border-slate-200 shadow-sm"
             data-testid="weekly-pdf-toggle-section">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg inline-flex items-center justify-center shrink-0"
               style={{ background: "linear-gradient(135deg, #0B1B2F 0%, #071B34 100%)" }}>
            <FileText className="w-5 h-5" style={{ color: "#F0D37A" }}/>
          </div>
          <div className="min-w-0">
            <h3 className="font-heading text-lg" style={{ color: "#0B1B2F" }}>
              Anexar PDF do Dashboard no resumo semanal
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Quando ativo, todo resumo semanal enviado por e-mail vem com um snapshot em PDF dos KPIs, cards centrais e footer do seu painel.
            </p>
          </div>
        </div>
        <button onClick={toggle} disabled={busy}
                data-testid="btn-toggle-weekly-pdf"
                aria-pressed={enabled}
                className={`relative shrink-0 w-14 h-7 rounded-full transition-colors ${enabled ? "" : "bg-slate-200"}`}
                style={enabled ? { background: "linear-gradient(180deg, #F5DE8C 0%, #D4AF37 100%)" } : undefined}>
          <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-7" : "translate-x-0.5"}`}/>
        </button>
      </div>
    </section>
  );
}

function PasswordChangeSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("As senhas novas não coincidem"); return; }
    if (next.length < 8) { toast.error("Senha nova mínimo 8 caracteres"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password",
        { current_password: current, new_password: next });
      toast.success("Senha alterada com sucesso");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao alterar senha");
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-6 bg-white rounded-2xl p-6 border border-slate-200 shadow-sm"
             data-testid="password-change-section">
      <h3 className="font-heading text-lg mb-4" style={{ color: "#0B1B2F" }}>
        Trocar senha
      </h3>
      <form onSubmit={submit} className="grid md:grid-cols-3 gap-3 items-end">
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Senha atual</label>
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                 data-testid="pw-current"
                 className="mt-1 w-full h-10 border border-slate-300 rounded-lg px-3 text-sm"/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Nova senha</label>
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                 data-testid="pw-new" required minLength={8}
                 className="mt-1 w-full h-10 border border-slate-300 rounded-lg px-3 text-sm"/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500">Confirmar</label>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                 data-testid="pw-confirm" required minLength={8}
                 className="mt-1 w-full h-10 border border-slate-300 rounded-lg px-3 text-sm"/>
        </div>
        <div className="md:col-span-3 flex justify-end">
          <button type="submit" disabled={busy || !next || !confirm}
            data-testid="btn-change-password"
            className="px-5 h-10 rounded-lg text-slate-950 text-sm font-semibold disabled:opacity-60 hover:brightness-105 transition"
            style={{ background: "linear-gradient(180deg, #F5DE8C 0%, #D4AF37 100%)",
                       boxShadow: "0 6px 16px -4px rgba(212,175,55,0.5)" }}>
            {busy ? "Alterando..." : "Alterar senha"}
          </button>
        </div>
      </form>
      <p className="text-[11px] text-slate-500 mt-3">
        Se sua conta foi criada via Google e você nunca definiu senha, deixe "Senha atual" em branco.
      </p>
    </section>
  );
}
